import React, { useState, useEffect, FormEvent, useCallback } from 'react';
import axiosInstance from 'config/axiosInstance';
import Search from 'reusable/search';
import SortBy from 'reusable/sortby';
import Pagination from 'reusable/pagination';
import Export from 'reusable/export';
import DeviceTable from './DeviceTable';
import AddButton from 'reusable/addButton';
import AddDialog from 'reusable/Dialog/addDialog';
import EditDialog from 'reusable/Dialog/editDialog';
import DeleteDialog from 'reusable/Dialog/deleteDialog';
import {
  DialogConfig,
  DialogField,
  DialogType,
  NewDataField,
  sortingOptions,
} from 'reusable/Dialog/dialogInterface';
import {
  addDeviceFields,
  deleteDeviceFields,
  editDeviceFields,
} from './DeviceField';
import { useTranslation } from 'react-i18next';
import { id } from 'date-fns/locale';

const DeviceManagement: React.FC = () => {
  const { t } = useTranslation(['device', 'common']);
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [deviceData, setDeviceData] = useState<any[]>([]);
  const [count, setCount] = useState(1);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<DialogConfig>({
    title: '',
    subtitle: '',
    fields: [],
  });
  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>('');

  const fetchProfilePicture = async (userId: string) => {
    try {
      const response = await axiosInstance.get(`/v1/drivers/${userId}/`);
      return response.data.profile_picture;
    } catch (error) {
      console.error(
        `Failed to fetch profile picture for user ${userId}`,
        error
      );
      return null;
    }
  };

  const fetchDeviceData = useCallback(async () => {
    try {
      let apiUrl = `/v1/devices/?order_by=${sortValue}&page=${currentPage}&page_size=10&search=${searchValue}`;

      const response = await axiosInstance.get(apiUrl);
      const fetchedDeviceData = response.data.results;

      const updatedDeviceData = await Promise.all(
        fetchedDeviceData.map(async (device: any) => {
          if (device.user_info?.id) {
            const profilePicture = await fetchProfilePicture(
              device.user_info.id
            );
            return {
              ...device,
              user_info: {
                ...device.user_info,
                profile_picture: profilePicture,
              },
            };
          }
          return device;
        })
      );

      setDeviceData(updatedDeviceData);
      setCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [searchValue, sortValue, currentPage]);

  useEffect(() => {
    fetchDeviceData();
  }, [fetchDeviceData, currentDeviceId]);

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const openDialog = (
    title: string,
    subtitle: string,
    fields: DialogField[],
    dialogType: DialogType
  ) => {
    setDialogProps({ title, subtitle, fields });
    if (dialogType === DialogType.Add) setIsAddDialogOpen(true);
    if (dialogType === DialogType.Edit) setIsEditDialogOpen(true);
    if (dialogType === DialogType.Delete) setIsDeleteDialogOpen(true);
  };

  const closeDialog = () => {
    setIsAddDialogOpen(false);
    setIsEditDialogOpen(false);
    setIsDeleteDialogOpen(false);
    setCurrentDeviceId(null);
  };

  const handleDialog = async (dialogType: DialogType, id?: string) => {
    let driverOptions: any[] = [];

    try {
      let driverApiUrl = `/v1/drivers/?page_size=0`;
      const fetchedDriverOptions = (await axiosInstance.get(driverApiUrl)).data;
      driverOptions = fetchedDriverOptions.map((driver: any) => ({
        id: driver.id,
        value: `using:${driver.id}`,
        label: driver.name,
      }));
    } catch (error) {
      console.error('Error fetching drivers data', error);
    }

    const assignUserOption = [
      { id: null, value: 'available', label: t('status.available') },
      { id: null, value: 'in_repair', label: t('status.in_repair') },
      { id: null, value: 'damaged', label: t('status.damaged') },
      ...driverOptions,
    ];

    if (dialogType === DialogType.Add) {
      openDialog(
        t('add-device.title'),
        '',
        addDeviceFields(t, {}, assignUserOption),
        DialogType.Add
      );
    } else if (
      (dialogType === DialogType.Edit || dialogType === DialogType.Delete) &&
      id
    ) {
      try {
        const deviceResponse = await axiosInstance.get(`/v1/devices/${id}/`);
        const deviceData = deviceResponse.data;
        setCurrentDeviceId(id);
        if (dialogType === DialogType.Edit) {
          if (deviceData.status === 'using') {
            deviceData.status = `using:${deviceData.user}`;
          }
          openDialog(
            t('edit-device.title'),
            '',
            editDeviceFields(t, deviceData, assignUserOption),
            DialogType.Edit
          );
        } else if (dialogType === DialogType.Delete) {
          openDialog(
            t('delete-device.title'),
            t('delete-device.subtitle'),
            deleteDeviceFields(t, deviceData),
            DialogType.Delete
          );
        }
      } catch (error) {
        console.error('Failed to fetch device data', error);
      }
    }
  };

  const handleAddDevice = async (
    e: FormEvent<Element>,
    newData: NewDataField
  ) => {
    e.preventDefault();

    let updatedStatus = newData.status;
    let driverId = null;

    if (
      typeof newData.status === 'string' &&
      newData.status.includes('using')
    ) {
      [updatedStatus, driverId] = newData.status.split(':');
    }

    newData.status = updatedStatus;
    newData.user = driverId;

    try {
      const response = await axiosInstance.post('/v1/devices/', newData);
      const newDevice = response.data;

      setIsAddDialogOpen(false);
      setDeviceData((prevData) => [...prevData, newDevice]);
    } catch (err: any) {
      console.log('Failed to add device:', err);
    }
  };

  const handleEditDevice = async (
    e: FormEvent<Element>,
    updatedData: NewDataField & { id: string }
  ) => {
    e.preventDefault();

    try {
      if (updatedData && updatedData.status !== null) {
        const statusValue = String(updatedData.status);

        const [updatedStatus, driverId] = statusValue.includes(':')
          ? statusValue.split(':')
          : [statusValue, null];

        updatedData.status = updatedStatus;
        updatedData.user = driverId;

        const response = await axiosInstance.patch(
          `/v1/devices/${updatedData.id}/`,
          updatedData
        );

        setDeviceData((prevData) =>
          prevData.map((device) =>
            device.id === updatedData.id ? response.data : device
          )
        );
      }

      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update device:', error);
    }
  };

  const handleDeleteDevice = async (id: string) => {
    try {
      await axiosInstance.delete(`/v1/devices/${id}/`);
      setDeviceData(deviceData.filter((device) => device.id !== id));
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete device:', error);
    }
  };

  return (
    <div>
      <div className="flex flex-row justify-between">
        <div className="flex w-2/5 justify-start">
          <Search
            placeholder={t('table.search')}
            onSearchChange={handleSearchChange}
          />
        </div>
        <div className="flex flex-row space-x-2 items-center">
          <SortBy
            options={sortingOptions(t)}
            defaultValue=""
            onChange={handleSortChange}
          />
          <AddButton
            label={t('add-device.title')}
            onClick={() => handleDialog(DialogType.Add)}
          />
          <AddDialog
            isOpen={isAddDialogOpen}
            onClose={closeDialog}
            handleSubmit={handleAddDevice}
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            confirmLabel={t('add-device.save')}
            cancelLabel={t('add-device.cancel')}
            fields={dialogProps.fields}
          />
          <EditDialog
            isOpen={isEditDialogOpen}
            onClose={closeDialog}
            handleSubmit={(e: any, updatedData: any) =>
              handleEditDevice(e, { ...updatedData, id: currentDeviceId ?? '' })
            }
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            confirmLabel={t('edit-device.save')}
            cancelLabel={t('edit-device.cancel')}
            fields={dialogProps.fields}
            deviceId={currentDeviceId ?? ''}
          />
          <DeleteDialog
            isOpen={isDeleteDialogOpen}
            onClose={closeDialog}
            handleSubmit={(e: any, data: any) =>
              handleDeleteDevice(data.id || '')
            }
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            confirmLabel={t('delete-device.save')}
            cancelLabel={t('delete-device.cancel')}
            fields={dialogProps.fields}
            deviceId={currentDeviceId ?? ''}
          />
          <Export
            data={deviceData}
            exportFields={[
              { header: 'Device ID', field: 'id' },
              { header: 'Device Name', field: 'name' },
              { header: 'Device Status', field: 'status' },
              { header: 'Assigned User Name', field: 'user_info.name' },
              { header: 'Assigned User Contact', field: 'user_info.email' },
              { header: 'Assigned Date', field: 'assigned_date' },
              { header: 'Purchase Date', field: 'purchase_date' },
              { header: 'Warranty Date', field: 'warranty_date' },
            ]}
          />
        </div>
      </div>

      <DeviceTable
        devices={deviceData}
        onEdit={handleDialog.bind(null, DialogType.Edit)}
        onDelete={handleDialog.bind(null, DialogType.Delete)}
      />

      <div>
        <Pagination
          allRows={count}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default DeviceManagement;
